import { RequestPayload } from '@inertiajs/core';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { CreditCard, Repeat } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { useMutation, useServerErrors } from '../../../Shared/api';
import { Editor } from '../../../Shared/Editor/Editor';
import { Modal } from '../../../Shared/Modal/Modal';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { donationFields } from '../../../Shared/types';
import { Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { Checkbox } from '../../../Shared/UI/Checkbox';
import { CheckboxBox } from '../../../Shared/UI/CheckboxBox';
import CheckboxLabel from '../../../Shared/UI/CheckboxLabel';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { Icon } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import InputDescription from '../../../Shared/UI/InputDescription';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { useRouter } from '../../router';
import { Charity, DonationForm } from '../../types';
import Layout from '../../UI/Layout';
import RecurringDonationsTerms from './RecurringDonationsTerms';

export interface EditDonationFormSettingsFormProps {
  donationForm: DonationForm;
  charity: Charity;
}

export default function EditDonationFormSettingsForm({
  donationForm,
  charity,
}: EditDonationFormSettingsFormProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const form = useForm({
    defaultValues: {
      internal_title: donationForm.internal_title,
      title: donationForm.title,
      description: donationForm.description,
      success_message: donationForm.success_message,
      allow_donations: donationForm.allow_donations,
      allow_subscriptions: donationForm.allow_subscriptions,
      fields: donationForm.fields,
      payment_reference: donationForm.payment_reference,
      campaign_code: donationForm.campaign_code,
    },
  });

  const {
    register, control, handleSubmit, watch,
    formState: { defaultValues, errors },
  } = form;

  const allowMonthly = watch('allow_subscriptions');

  const paymentReference = watch('payment_reference');
  const paymentReferenceLength = paymentReference?.length || 0;
  const maxPaymentReferenceLength = 20;

  const [editDonationForm, { errors: serverErrors, loading }] = useMutation(
    routes.edit_donation_form_settings(donationForm.id),
    {
      onSuccess: () => {
        notifier.notify(t('backend:donation_form_saved'));
      },
    }
  );

  useServerErrors(form, serverErrors);

  const [formData, setFormData] = useState<RequestPayload>();

  const submit = async (data: typeof defaultValues) => {
    const formData = {
      title: data?.title,
      internal_title: data?.internal_title,
      description: data?.description,
      success_message: data?.success_message,
      allow_donations: data?.allow_donations || !data?.allow_subscriptions,
      allow_subscriptions: data?.allow_subscriptions,
      fields: data?.fields,
      payment_reference: data?.payment_reference,
      campaign_code: data?.campaign_code,
    };

    if (data?.allow_subscriptions && !charity.has_subscriptions && !showTermsModal) {
      setFormData(formData);
      setShowTermsModal(true);
    } else {
      await editDonationForm(formData);
    }
  };

  const [showTermsModal, setShowTermsModal] = useState(false);

  const acceptTermsAndContinue = () => {
    setShowTermsModal(false);
    editDonationForm(formData);
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Card panels header footer>
        <div>
          <h3>
            {t('backend:edit_donation_form')}
          </h3>
        </div>
        {serverErrors && (
          <div>
            <ServerErrors errors={serverErrors} defaultValues={defaultValues} scrollIntoView />
          </div>
        )}
        <div className="grid grid-cols-[1fr_2fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:general')}
            </h4>
            <div className="text-slate-500">
              {t('backend:donation_form_general_description')}
            </div>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel required valid={!errors.internal_title} htmlFor="edit_donation_form_internal_title">
                {t('backend:internal_title')}
              </InputLabel>
              <InputDescription>
                {t('backend:donation_form_internal_title_description')}
              </InputDescription>
              <Input
                {...register('internal_title', { required: true })}
                id="edit_donation_form_internal_title"
                aria-invalid={!!errors.internal_title}
              />
              <ErrorMessage error={errors.internal_title} attribute={t('backend:internal_title')} />
            </InputGroup>

            <InputGroup>
              <InputLabel required valid={!errors.title} htmlFor="edit_donation_form_title">
                {t('backend:external_title')}
              </InputLabel>
              <InputDescription>
                {t('backend:donation_form_title_description')}
              </InputDescription>
              <Input
                {...register('title', { required: true })}
                id="edit_donation_form_title"
                aria-invalid={!!errors.title}
              />
              <ErrorMessage error={errors.title} attribute={t('backend:external_title')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_donation_form_description">
                {t('backend:description')}
              </InputLabel>
              <InputDescription>
                {t('backend:donation_form_description_description')}
              </InputDescription>
              <Controller
                control={control}
                name="description"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Editor
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
              <ErrorMessage error={errors.description} attribute={t('backend:description')} />
            </InputGroup>

            <InputGroup>
              <InputLabel required valid={!errors.allow_donations}>
                {t('backend:donation_types')}
              </InputLabel>
              <div className="space-y-1">
                <CheckboxBox
                  htmlFor="allow_donations"
                  className="relative px-3 py-2 overflow-hidden bg-white border rounded-md border-slate-250"
                >
                  <Checkbox
                    {...register('allow_donations', { validate: (allowOneoff) => allowOneoff || allowMonthly })}
                    id="allow_donations"
                    className="mt-[3px] mr-2"
                    aria-invalid={!!errors.allow_donations}
                  />
                  <div className="pr-[80px]">
                    <strong className="font-semibold">
                      {t('backend:oneoff_donations')}
                    </strong>
                    <div>
                      {t('backend:accept_oneoff_donations_description')}
                    </div>
                  </div>
                  <div className="rounded-l-full w-[100px] h-[160px] bg-blue-100 text-blue-500 flex items-center justify-center absolute -right-6 pr-5 top-1/2 -mt-[80px] text-2xl">
                    <Icon>
                      <CreditCard strokeWidth={1.5} />
                    </Icon>
                  </div>
                </CheckboxBox>
                <CheckboxBox
                  htmlFor="allow_subscriptions"
                  className="relative px-3 py-2 overflow-hidden bg-white border rounded-md border-slate-250"
                >
                  <Checkbox
                    {...register('allow_subscriptions')}
                    id="allow_subscriptions"
                    className="mt-[3px] mr-2"
                  />
                  <div className="pr-[80px]">
                    <strong className="font-semibold">
                      {t('backend:recurring_donations')}
                    </strong>
                    <div>
                      {t('backend:accept_recurring_donations_description')}
                    </div>
                  </div>
                  <div className="rounded-l-full w-[100px] h-[160px] bg-blue-100 text-blue-500 flex items-center justify-center absolute -right-6 pr-5 top-1/2 -mt-[80px] text-2xl">
                    <Icon>
                      <Repeat strokeWidth={1.5} />
                    </Icon>
                  </div>
                </CheckboxBox>
              </div>
              <ErrorMessage
                error={errors.allow_donations}
                message={errors.allow_donations ? t('shared:validation.select_at_least_one_option') : null}
              />
            </InputGroup>

            <InputGroup>
              <InputLabel valid={!errors.fields}>
                {t('backend:fields')}
              </InputLabel>
              {donationFields.map((donationField) => (
                <CheckboxLabel htmlFor={`donation_field_${donationField}`} key={donationField}>
                  <Checkbox
                    {...register('fields')}
                    value={donationField}
                    id={`donation_field_${donationField}`}
                  />
                  <span>
                    {t(`backend:donation_form_fields.${donationField}`)}
                  </span>
                </CheckboxLabel>
              ))}
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_donation_form_success_message">
                {t('backend:success_message')}
              </InputLabel>
              <InputDescription>
                {t('backend:donation_form_success_message_description')}
              </InputDescription>
              <Controller
                control={control}
                name="success_message"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Editor
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
              <ErrorMessage error={errors.success_message} attribute={t('backend:success_message')} />
            </InputGroup>
          </div>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-8">
          <div>
            <div>
              <h4 className="font-medium text-slate-500 mb-1">
                {t('backend:payments')}
              </h4>
              <div className="text-slate-500">
                {t('backend:donation_payments_description')}
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel valid={!errors.campaign_code} htmlFor="edit_donation_form_campaign_code">
                {t('backend:campaign_code')}
              </InputLabel>
              <InputDescription>
                {t('backend:campaign_code_description')}
              </InputDescription>
              <Input
                {...register('campaign_code')}
                id="edit_donation_form_campaign_code"
                aria-invalid={!!errors.campaign_code}
              />
              <ErrorMessage attribute={t('backend:campaign_code')} error={errors.campaign_code} />
            </InputGroup>

            <InputGroup>
              <InputLabel valid={!errors.payment_reference} htmlFor="edit_donation_form_payment_reference">
                {t('backend:payment_reference')}
              </InputLabel>
              <InputDescription>
                {t('backend:donation_form_payment_reference_description')}
                {' '}
                <span className={classNames('bg-blue-200 text-blue-700 px-1 inline-block text-sm rounded-sm', paymentReferenceLength === 0 && 'opacity-50')}>
                  <strong>{paymentReferenceLength}</strong> / {maxPaymentReferenceLength}
                </span>
              </InputDescription>
              <Input
                {...register('payment_reference')}
                id="edit_donation_form_payment_reference"
                maxLength={maxPaymentReferenceLength}
                aria-invalid={!!errors.payment_reference}
                placeholder={charity.payment_reference || ''}
              />
              <div className="mt-2">
                <Trans i18nKey="backend:example_payment_reference" values={{ reference: `${paymentReference || charity.payment_reference || ''} 12345 ${charity.title}` }}>
                  <span className="font-medium text-slate-500"/>
                </Trans>
              </div>
              <ErrorMessage attribute={t('backend:payment_reference')} error={errors.payment_reference} />
            </InputGroup>
          </div>
        </div>
        <div className="text-right">
          <Button
            type="submit"
            loading={loading}
            className="ml-auto"
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>

      <Modal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} size="sm">
        <RecurringDonationsTerms
          charity={charity}
          onAccept={() => acceptTermsAndContinue()}
          onRefuse={() => setShowTermsModal(false)}
          loading={loading}
        />
      </Modal>
    </form>
  );
}

EditDonationFormSettingsForm.layout = (page: ReactNode) => <Layout>{page}</Layout>;
